import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/** A key's page, whichever of its tabs is showing. */
const KEY_PAGE = /^\/containers\/[^/]+\/keys\/[^/]+/;

/** Both paths are tabs of the same key's page: the list at /keys, or another key, is not. */
function sameKeyPage(from: string, to: string): boolean {
  const page = KEY_PAGE.exec(from)?.[0];
  return page !== undefined && page === KEY_PAGE.exec(to)?.[0];
}

/**
 * Moves focus to the main region on navigation.
 *
 * A client-side route change replaces the page without telling assistive technology
 * anything happened: focus stays on the link that was clicked, which no longer exists,
 * and the announcement never comes. Focusing <main> is what makes a SPA navigation read
 * like a page load.
 *
 * Except between a key's tabs. Each has its own URL, but the page stays: its header and
 * the tab strip are still there, the tab that was pressed keeps focus, and its
 * aria-current already says which section is showing. Moving focus to <main> would only
 * send a keyboard user back to the top of the page they are on. The scroll stays too.
 *
 * The first render is skipped deliberately. On a cold load the browser has already put
 * focus where it belongs, and stealing it would move a keyboard user off the skip link
 * they were about to use. Comparing against the last path, rather than flagging the first
 * run, also keeps strict mode's second run of the effect from counting as a navigation.
 */
export function RouteFocus({ targetId = 'main' }: { targetId?: string }) {
  const { pathname } = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const from = previous.current;
    previous.current = pathname;
    if (from === null || from === pathname || sameKeyPage(from, pathname)) return;
    const el = document.getElementById(targetId);
    if (!el) return;
    el.focus({ preventScroll: true });
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, targetId]);

  return null;
}

export default RouteFocus;
